import { Stack, Text, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconClockFilled } from '@tabler/icons-react'
import React, { memo, useCallback, useEffect, useState } from 'react'

import classes from './Timer.module.css'

type TimerProps = {
  /**
   * Callback for when this component ends. This function reference
   * doesn't need to be stable, although that'd be advisable.
   */
  text?: string
  secondary?: boolean
  onTimeout?: () => void
} & (
  | {
      /**
       * Expiration timestamp in Epoch seconds
       */
      expiryTimestamp: number
      initialSeconds?: undefined
    }
  | {
      expiryTimestamp?: undefined
      /**
       * Number of seconds since the component is mounted.
       * If the Timer doesn't get unmounted as a side-effect
       * of onTimeout, it will reset and loop forever.
       */
      initialSeconds: number
    }
)

const Timer: React.FC<TimerProps> = ({
  text = 'remaining',
  expiryTimestamp,
  initialSeconds,
  secondary,
  onTimeout,
}) => {
  const theme = useMantineTheme()
  // Calculates remaining seconds based on either an expiry timestamp or initial minutes.
  // This calculation is in a memoized function, so its value doesn't change on every
  // render if `expiryTimestamp` was used (seconds left would depend on the current time).
  const getSecondsLeft = useCallback(
    () =>
      initialSeconds
        ? Math.max(0, initialSeconds)
        : expiryTimestamp
          ? Math.floor(expiryTimestamp - Date.now() / 1000)
          : 0,
    [expiryTimestamp, initialSeconds]
  )
  const [time, setTime] = useState(getSecondsLeft)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`)

  const [triggerTimeout, setTriggerTimeout] = useState(false)
  useEffect(() => {
    if (triggerTimeout) {
      onTimeout?.()
      setTriggerTimeout(false)
    }
  }, [onTimeout, triggerTimeout])

  // This effect, which controls a JS interval, should only be reset if
  // either `expiryTimestamp` or `initialSeconds` change, in which case,
  // `getSecondsLeft` will change its reference, making the effect start over.
  // This effect should not re-render every second or when the `onTimeout`
  // function changes. This is avoided by using a reference.
  useEffect(() => {
    const counterSeconds = getSecondsLeft()

    // Don't even try to start counting down if this timer is for 0 or
    // negative seconds, i.e. `expiryTimestamp` is in the past, or
    // `initialSeconds` is 0 or negative.
    if (counterSeconds <= 0) {
      setTime(0)
      return undefined
    }

    setTime(counterSeconds)

    const interval = setInterval(() => {
      setTime(prevTime => {
        let newTime = prevTime - 1

        if (newTime <= 0) {
          // If the timer is set with `initialSeconds`, then `getSecondsLeft`
          // will return the same number again
          const newCounterSeconds = getSecondsLeft()

          // If the timer was set with `expiryTimestamp`, `newCounterSeconds`
          // will be 0, in which case, the timer shouldn't reset.
          if (newCounterSeconds <= 0) {
            clearInterval(interval)
          }
          // Else, if the timer was set with `initialSeconds` (i.e.:
          // `newCounterSeconds > 0`), the timer should restart.
          else {
            newTime = newCounterSeconds
          }

          // Do this instead of calling the onTimeout function directly,
          // so it doesn't block the UI. setTime should return ASAP,
          // and the onTimeout function could also change the UI,
          // creating a bug in the component's lifecycle (think
          // the onTimeout function unmounts this component: it would
          // unmount the components and then setTime would return but
          // the component wouldn't exist anymore, aka a bug).
          setTriggerTimeout(true)
        }

        return newTime > 0 ? newTime : 0
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [getSecondsLeft])

  const days = Math.floor(time / 86400)
  const hours = Math.floor((time % 86400) / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = time % 60

  const minsSeconds = `${secondary ? `${days.toString().padStart(2, '0')}d ` : ''}${secondary ? `${hours.toString().padStart(2, '0')}h ` : ''}${minutes
    .toString()
    .padStart(
      2,
      '0'
    )}${secondary && 'm'}${secondary ? ' ' : ':'}${seconds.toString().padStart(2, '0')}${secondary && 's'}`

  return (
    <Stack className={classes.timer} style={{ backgroundColor: secondary ? 'transparent' : '' }}>
      <IconClockFilled
        className={classes.icon}
        color={theme.colors.paragraph[0]}
        style={{ width: secondary ? '20px' : '', height: secondary ? '20px' : '' }}
      />
      <Stack className={classes.timerContainer}>
        {secondary && (
          <Text
            className={classes.text}
            style={{
              color: secondary ? theme.colors.paragraph[0] : '',
            }}
          >
            {text}
          </Text>
        )}
        {isMobile ? (
          <Text
            style={{
              fontFamily: 'monument-grotesk-bold, sans-serif',
              color: secondary ? theme.colors.paragraph[0] : '',
              width: secondary ? '130px' : '',
            }}
            className={classes.timerText}
          >
            {minsSeconds}
          </Text>
        ) : (
          <>
            <Text
              style={{
                fontFamily: 'monument-grotesk-bold, sans-serif',
                color: theme.colors.paragraph[0],
                width: secondary ? '130px' : '',
              }}
              className={classes.timerText}
              i18nIsDynamicList
            >
              {minsSeconds}
            </Text>{' '}
            {!secondary && (
              <Text
                className={classes.text}
                style={{
                  color: secondary ? theme.colors.paragraph[0] : '',
                }}
              >
                {text}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Stack>
  )
}

export default memo(Timer)
